import { Issue } from "generated-graphql/graphql";
import { useCallback } from "react";
import { FacilityDetailsInput } from "routes/Customer/Facility/FacilityProfile";

import { useFacilityInputValidation } from "./validationHooks/useFacilityInputValidation";

export const regulatoryFieldsExceptFacilityAlternateIds = [
  "isSubjectToChemicalAccidentPrevention",
  "isSubjectToEmergencyPlanning",
  "isSubjectToToxicsReleaseInventoryReporting",
];

// For report validation, these fields that are Regulatory Info fields and
// are not validated on the Facility Profile page
export const regulatoryFields = [
  ...regulatoryFieldsExceptFacilityAlternateIds,
  "facilityAlternateIds",
];

/** Facility schema keys other Tier II sections own, skipped when validating the Facility section. */
const FACILITY_INFO_VALIDATION_OMIT = [
  "facilityDocuments",
  "documents",
  "contacts",
  "stateFields",
  "undergroundStorageTanks",
  ...regulatoryFields,
];

/** Whether an issue belongs on the Tier II report's Facility section. */
export function isFacilityInfoIssue(issue: Issue): boolean {
  return (
    issue.modelName === "Facility" &&
    issue.key !== "contacts" &&
    issue.key !== "documents" &&
    issue.key !== "facilityDocuments" &&
    issue.key !== "reportFacilityDocuments" &&
    issue.key !== "facilityChemicals" &&
    issue.key !== "undergroundStorageTanks" &&
    // these are the errors related to the actual whole of the list of
    // reporting facility chemicals, currently it's just a validation that
    // there is at least 1 RFC, so we dont want that on the facility tab
    issue.key !== "reportingFacilityChemicals" &&
    // the RCRA facility profile is a separate regulatory concern surfaced on
    // the facility profile page (see useRcraProfileIssues), not the Tier II report
    issue.key !== "facilityRcraProfile" &&
    !regulatoryFields.some((re) => issue.key?.startsWith(re))
  );
}

/** Live validation for the Facility section, showing the same issues as `useFacilityInfoIssues`. */
export function useFacilityInfoValidation({
  facilityId,
  reportId,
}: {
  facilityId: string;
  reportId: string | undefined;
}) {
  const validate = useFacilityInputValidation({
    facilityId,
    reportId,
    omit: FACILITY_INFO_VALIDATION_OMIT,
  });
  return useCallback(
    async (input: FacilityDetailsInput) => (await validate(input)).filter(isFacilityInfoIssue),
    [validate]
  );
}
